import { FC, useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import { getLink } from '__components/Timeline2/EventBlocks/helpers';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { useTranslation } from 'react-i18next';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import {
  ExecutionStatus,
  ScriptFile,
  SmartConnectorExecution,
  SmartConnectorMetaData,
  TeamMember,
} from '__pages/SmartConnectors/types';
import { EXECUTION_ACTIONS } from './constants';
import {
  SMART_CONNECTOR_EXECUTION_STATUSES,
  SMART_FIELD_TYPES,
} from '__pages/SmartConnectors/constants';
import ActionCell from '__components/Tables/ActionCell';
import { getExecutionProgressString } from './helpers';
import { KizenTypography } from '__app/typography';

const EMPTY_VALUE = '—';

const isCancellable = (status?: ExecutionStatus) =>
  Boolean(
    status &&
      !(
        [
          SMART_CONNECTOR_EXECUTION_STATUSES.success,
          SMART_CONNECTOR_EXECUTION_STATUSES.cancelled,
          SMART_CONNECTOR_EXECUTION_STATUSES.failed,
        ] as ExecutionStatus[]
      ).includes(status)
  );

const ControlsCell: FC<{
  onSelectAction: (
    action: { value: keyof typeof EXECUTION_ACTIONS },
    data: SmartConnectorExecution
  ) => void;
  data?: SmartConnectorExecution;
  access: { edit: boolean; remove: boolean };
}> = ({ onSelectAction, data, access, ...props }) => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      [
        {
          label: t('View Metadata'),
          value: EXECUTION_ACTIONS.view,
        },
        data?.input_file?.url && {
          label: t('Download Input File'),
          value: EXECUTION_ACTIONS.download_input,
        },
        data?.final_report?.url && {
          label: t('Download Report'),
          value: EXECUTION_ACTIONS.download_report,
        },
        access.edit &&
          isCancellable(data?.status) && {
            label: t('Cancel Execution'),
            value: EXECUTION_ACTIONS.cancel,
          },
      ].filter(Boolean),
    [t, access, data]
  );
  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

const StatusCell: FC<{
  handleClick: (e: React.MouseEvent) => void;
  status: ExecutionStatus;
  metaData: SmartConnectorMetaData;
  execution: SmartConnectorExecution;
}> = ({ status, handleClick, metaData, execution }) => {
  const { t } = useTranslation();
  const progress = useMemo(() => {
    if (status === SMART_CONNECTOR_EXECUTION_STATUSES.in_progress) {
      return getExecutionProgressString(execution.step_progress, metaData, t);
    }
    return '';
  }, [status, t, metaData, execution.step_progress]);

  return (
    <TextEllipsisWithTooltip type="anchor" href="" onClick={handleClick}>
      {`${metaData.execution_flow.execution_status[status]} ${progress}`}
    </TextEllipsisWithTooltip>
  );
};

type useColumnsProps = {
  onSelectAction: (
    action: { value: keyof typeof EXECUTION_ACTIONS },
    data: SmartConnectorExecution
  ) => void;
  onViewDetails: (data: SmartConnectorExecution) => void;
  access: { edit: boolean; remove: boolean };
  metaData: SmartConnectorMetaData;
};

export const useColumns = ({
  onSelectAction,
  onViewDetails,
  access,
  metaData,
}: useColumnsProps) => {
  const { t } = useTranslation();
  const preReleaseFeatures = usePreReleaseFeatures();
  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="15px" />,
        cell: <SizedCell width="15px" />,
        format: () => <span />,
      },
      {
        id: 'input_file',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Input File')}
            width="120px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="120px" padding="30px" />,
        format: (input_file: ScriptFile) => {
          return (
            <TextEllipsisWithTooltip>
              {input_file?.name || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'status',
        headCell: (
          <SortableHeadCell
            by="status"
            label={t('Status')}
            width="170px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="170px" padding="30px" />,
        format: (status: ExecutionStatus, data: SmartConnectorExecution) => {
          const handleClick = (e: React.MouseEvent) => {
            e.preventDefault();
            onViewDetails(data);
          };
          return (
            <StatusCell
              status={status}
              handleClick={handleClick}
              metaData={metaData}
              execution={data}
            />
          );
        },
      },
      {
        id: 'started_by',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Started By')}
            width="200px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="200px" padding="30px" />,
        format: (started_by: TeamMember) => {
          return started_by ? (
            <TextEllipsisWithTooltip
              {...(preReleaseFeatures
                ? {
                    type: 'anchor',
                    href: getLink(
                      {
                        id: started_by.id,
                        name: started_by.display_name,
                      },
                      {
                        type: SMART_FIELD_TYPES.team_selector,
                        metaInfo: null,
                      }
                    ),
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
                : {})}
            >
              {started_by.display_name}
            </TextEllipsisWithTooltip>
          ) : (
            <KizenTypography>{EMPTY_VALUE}</KizenTypography>
          );
        },
      },
      {
        id: 'created',
        headCell: (
          <SortableHeadCell
            by="created"
            numeric
            label={t('Started At')}
            width="150px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="150px" padding="30px" />,
        format: (created: any) => {
          return created ? (
            <DateTimeInputInline readOnly value={created} showDateTooltip />
          ) : (
            <KizenTypography>{EMPTY_VALUE}</KizenTypography>
          );
        },
      },
      {
        id: 'ended_at',
        headCell: (
          <SortableHeadCell
            by="ended_at"
            numeric
            label={t('Completed At')}
            width="150px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="150px" padding="30px" />,
        format: (ended_at: string) => {
          return ended_at ? (
            <DateTimeInputInline readOnly value={ended_at} showDateTooltip />
          ) : (
            <KizenTypography>{EMPTY_VALUE}</KizenTypography>
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={' '}
            width="15px"
            padding="15px"
          />
        ),
        cell: <ControlsCell onSelectAction={onSelectAction} access={access} />,
      },
    ],
    [preReleaseFeatures, onSelectAction, onViewDetails, access, metaData, t]
  );
};
